import { TimeInput, type TimeInputProps } from '@heroui/react'
import { parseTime } from '@internationalized/date'
import { I18nProvider } from '@react-aria/i18n'
import { useTranslation } from 'react-i18next'
import { dayFirstLocale } from './DateField'

type TimeFieldProps = Omit<TimeInputProps, 'value' | 'defaultValue' | 'onChange' | 'hourCycle'> & {
  /** `HH:mm`, 24-hour. Empty means no time. */
  value: string
  onValueChange: (value: string) => void
}

// Same cast as DateField: the @internationalized/date classes reach HeroUI
// through more than one copy, so identical classes look unrelated to TypeScript.
type PickerValue = NonNullable<TimeInputProps['value']>

function toTimeValue(value: string): PickerValue | null {
  if (!value) return null
  try {
    return parseTime(value.slice(0, 5)) as unknown as PickerValue
  } catch {
    return null
  }
}

/**
 * A time of day, always on the 24-hour clock. Replaces `<Input type="time">`,
 * which follows the viewer's OS and shows AM/PM on a US-locale laptop.
 */
export function TimeField({ value, onValueChange, ...props }: TimeFieldProps) {
  const { i18n } = useTranslation()
  return (
    <I18nProvider locale={dayFirstLocale(i18n.resolvedLanguage ?? i18n.language)}>
      <TimeInput
        {...props}
        hourCycle={24}
        value={toTimeValue(value)}
        onChange={(next) => onValueChange(next ? next.toString().slice(0, 5) : '')}
      />
    </I18nProvider>
  )
}
